import { APP_ROUTES } from '../../../app/routePaths';
import { callPortLabel, formatPtpTime, portWithTerminal } from '../../vessel-master';
import type { VesselAlert } from '../types/vesselChanges';

/** My alerts open my jobs; a team member's escalated alert opens the team view. */
export const vesselAlertLink = (alert: Pick<VesselAlert, 'mine'>): string =>
  alert.mine ? APP_ROUTES.vesselChanges : `${APP_ROUTES.vesselChanges}?scope=team`;

/** The job a LINK alert (or an ETA alert on jobs with no employee) points at; null when there is none. */
export const vesselAlertJobLink = (alert: Pick<VesselAlert, 'saleOrderId'>): string | null =>
  alert.saleOrderId ? APP_ROUTES.saleOrderEdit(alert.saleOrderId) : null;

/**
 * The row's only colour (owner, 2026-10-13: the bell must stay calm): a thin left border, blue for a
 * job to link, red for my own alert (ETA or OMITTED), amber for a team member's escalated to me.
 */
export const vesselAlertTone = (alert: Pick<VesselAlert, 'mine' | 'kind'>): string => {
  if (alert.kind === 'LINK') return 'border-l-blue-400';
  return alert.mine ? 'border-l-red-500' : 'border-l-amber-400';
};

/** A team member's name for an escalated alert; null for my own, for "No employee" and when the server sent none. */
export const vesselAlertTeamName = (alert: Pick<VesselAlert, 'mine' | 'unassigned' | 'employeeName'>): string | null => {
  if (alert.mine || alert.unassigned) return null;
  return alert.employeeName?.trim() || null;
};

/** When the alert was sent: only the time today (`15:42`), else `13 Oct 15:42`. */
export const vesselAlertSentLabel = (sentAt: string | null, today: Date = new Date()): string => {
  if (!sentAt) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const todayText = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const time = /^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2})/.exec(sentAt);
  if (time && sentAt.startsWith(todayText)) return time[1];
  return formatPtpTime(sentAt);
};

/** "ETA 17 Oct 06:00 (est.)"; empty when PTP gave no arrival. */
export const vesselAlertArrival = (alert: Pick<VesselAlert, 'arriveAt' | 'arriveEstimated'>): string => {
  const arrival = formatPtpTime(alert.arriveAt);
  if (!arrival) return '';
  return `${arrival}${alert.arriveEstimated ? ' (est.)' : ''}`;
};

/** "PTP", "Westports" or "MMSW". Without a label: from `port`; only Westports omits calls. */
export const vesselAlertPort = (alert: Pick<VesselAlert, 'portLabel' | 'port' | 'kind'>): string => {
  if (!alert.portLabel && alert.kind === 'OMITTED') return 'Westports';
  return callPortLabel(alert);
};

const isMmsw = (alert: Pick<VesselAlert, 'portLabel' | 'port' | 'kind'>): boolean => vesselAlertPort(alert) === 'MMSW';

/** The port with an MMSW terminal: "MMSW · Northport KCT"; other ports' terminals have their own part. */
export const vesselAlertPortText = (alert: Pick<VesselAlert, 'portLabel' | 'port' | 'kind' | 'terminal'>): string =>
  isMmsw(alert) ? portWithTerminal('MMSW', alert.terminal) : vesselAlertPort(alert);

/** "Terminal B18, LBT B7X" for jobs at Westports terminals; null otherwise (MMSW's is in the port text). */
export const vesselAlertTerminal = (alert: Pick<VesselAlert, 'portLabel' | 'port' | 'kind' | 'terminal'>): string | null =>
  alert.terminal && !isMmsw(alert) ? `Terminal ${alert.terminal}` : null;

const jobCount = (count: number) => `${count} ${count === 1 ? 'job' : 'jobs'}`;

/**
 * Line 2 of an ETA or OMITTED alert: "Westports ETA 17 Oct 06:00 (est.) · 2 jobs not updated · Terminal B18",
 * "MMSW · Northport KCT ETA 15 Oct 08:00 (est.) · 1 job not updated", or "Omitted at Westports · 2 jobs to move · Terminal B18".
 */
export const vesselAlertDetails = (alert: VesselAlert): string[] => {
  const port = vesselAlertPortText(alert);
  const parts = alert.kind === 'OMITTED'
    ? [`Omitted at ${port}`, `${jobCount(alert.jobs)} to move`]
    : [`${port} ETA ${vesselAlertArrival(alert) || 'not given'}`, `${jobCount(alert.jobs)} not updated`];
  const terminal = vesselAlertTerminal(alert);
  return terminal ? [...parts, terminal] : parts;
};
